class QuickAdd extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.trigger = this.querySelector('.v-quick-add__trigger');
    this.content = this.querySelector('.v-quick-add__content');
    this.form = this.querySelector('.v-product-form__form');
    this.submitBtn = this.querySelector('.v-quick-add__submit');
    this.variantRadios = this.querySelectorAll('.m-product-option__input[type="radio"]');
    
    if (!this.trigger || !this.content) return;
    
    this.productId = this.querySelector('[data-product-id]')?.dataset.productId;
    this.variantInput = this.querySelector('input[name="id"]');
    
    try {
      this.variants = JSON.parse(this.variantInput?.dataset.productVariants || '[]');
    } catch (e) {
      console.error('Error parsing variants:', e);
      this.variants = [];
    }
    
    this.bindEvents();
    this.updateSubmitButton();
  }

  bindEvents() {
    // Prevent clicks within quick-add content from bubbling to product card link
    this.content?.addEventListener('click', (e) => {
      if (!e.target.closest('button[type="submit"]')) {
        e.stopPropagation();
      }
    });

    // Handle variant radio interactions
    this.variantRadios?.forEach(radio => {
      radio.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      radio.addEventListener('change', (e) => {
        e.stopPropagation();
        this.handleVariantChange();
      });
    });

    // Handle trigger click
    this.trigger?.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.toggleContent(e);
    });

    // Handle click outside
    document.body.addEventListener('click', (e) => {
      if (!this.contains(e.target) && this.content.classList.contains('is-active')) {
        this.hideContent();
      }
    });

    // Handle form submission
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Check if all required options are selected
      const allOptionsSelected = this.allOptionsSelected();
      
      if (!allOptionsSelected) {
        this.submitBtn.classList.add('shake');
        setTimeout(() => this.submitBtn.classList.remove('shake'), 820);
        return;
      }
      
      this.handleSubmit();
    });
  }

  toggleContent(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (this.content.classList.contains('is-active')) {
      this.hideContent();
    } else {
      this.showContent();
    }
  }

  showContent() {
    // Hide any other open quick-add contents and show their triggers
    document.querySelectorAll('quick-add').forEach(quickAdd => {
      if (quickAdd !== this) {
        const content = quickAdd.querySelector('.v-quick-add__content');
        const trigger = quickAdd.querySelector('.v-quick-add__trigger');
        
        if (content?.classList.contains('is-active')) {
          content.classList.remove('is-active');
        }
        if (trigger?.classList.contains('is-hidden')) {
          trigger.classList.remove('is-hidden');
        }
      }
    });
    
    // Hide the trigger button
    this.trigger.classList.add('is-hidden');
    
    // Show the content
    this.content.classList.add('is-active');
    
    // Reset submit button state
    this.updateSubmitButton();
  }

  hideContent() {
    // Hide the content
    this.content.classList.remove('is-active');
    
    // Show the trigger button
    this.trigger.classList.remove('is-hidden');
  }

  updateSubmitButton() {
    const allOptionsSelected = this.allOptionsSelected();
    
    if (!allOptionsSelected) {
      this.submitBtn.querySelector('.v-quick-add__submit-text').textContent = 'SELECT OPTIONS';
      this.submitBtn.classList.add('not-selected');
      this.submitBtn.disabled = false;
      this.variantInput.value = ''; // Clear variant ID when no options selected
      return;
    }
    
    const variant = this.getSelectedVariant();
    if (variant) {
      this.submitBtn.classList.remove('not-selected');
      this.submitBtn.disabled = !variant.available;
      this.variantInput.value = variant.id;
      this.submitBtn.querySelector('.v-quick-add__submit-text').textContent = 
        variant.available ? 'ADD TO CART' : 'SOLD OUT';
    }
  }

  // Determine if each option group has a checked radio
  allOptionsSelected() {
    const groups = this.getOptionGroups();
    if (!groups.length) return false;
    return groups.every(name => !!this.form.querySelector(`input[name="${CSS.escape(name)}"]:checked`));
  }

  // Return unique option group names (e.g., options[Size], options[Color])
  getOptionGroups() {
    const names = Array.from(this.variantRadios).map(r => r.name).filter(Boolean);
    return Array.from(new Set(names));
  }

  getSelectedVariant() {
    if (!this.variants.length) return null;

    // Build selected options array in product option order by reading the DOM groups order
    const groups = this.getOptionGroups();
    const selectedOptions = groups.map(name => {
      const checked = this.form.querySelector(`input[name="${CSS.escape(name)}"]:checked`);
      return checked ? checked.value : '';
    });

    return this.variants.find(variant => variant.options.every((opt, i) => opt === selectedOptions[i]));
  }

  handleVariantChange() {
    const variant = this.getSelectedVariant();
    if (variant) {
      this.variantInput.value = variant.id;
    }
    this.updateSubmitButton();
  }

  async handleSubmit() {
    if (this.submitBtn.classList.contains('is-loading')) return;
    if (this.submitBtn.classList.contains('not-selected')) return;
    if (!this.variantInput.value) return;

    this.submitBtn.classList.add('is-loading');

    try {
      const formData = new FormData(this.form);
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        // Trigger cart update event
        window.dispatchEvent(new CustomEvent('cart:refresh'));
        this.hideContent();
      } else {
        console.error('Error:', data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.submitBtn.classList.remove('is-loading');
    }
  }

  // Public method to select a variant
  selectVariant(value) {
    this.showContent();
    const colorInput = this.form.querySelector(`input[name="${CSS.escape('options[Color]')}"][value="${CSS.escape(value)}"]`);
    if (colorInput) {
      colorInput.checked = true;
      colorInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

customElements.define('quick-add', QuickAdd);

// Handle swatch clicks only for product cards
document.addEventListener('click', (e) => {
  const swatch = e.target.closest('.m-product-card .m-product-option--node__label');
  if (swatch) {
    e.preventDefault();
    e.stopPropagation();
    
    const value = swatch.getAttribute('data-value');
    if (value) {
      const productCard = swatch.closest('.m-product-card');
      const quickAdd = productCard?.querySelector('quick-add');
      if (quickAdd) {
        quickAdd.selectVariant(value);
      }
    }
  }
});

